import { useEffect, useState } from 'react';
import { MdNotifications, MdSend } from 'react-icons/md';
import DataTable from '../../components/common/DataTable';
import { addNotification, calculateNotificationStats, subscribeToNotifications } from '../../services/notificationService';
import { emailService } from '../../services/emailService';
import { fetchUsers } from '../../services/userService';

const Notifications = () => {
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    audience: 'all',
    customClass: '',
    sendEmail: false,
  });
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    sent: 0,
    scheduled: 0,
    failed: 0,
    todaySent: 0,
  });

  // Subscribe to real-time notifications
  useEffect(() => {
    setLoading(true);
    const unsubscribe = subscribeToNotifications((data) => {
      setNotifications(data);
      setStats(calculateNotificationStats(data));
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleSendNotification = async () => {
    if (!formData.title || !formData.message) {
      alert('Please fill in title and message');
      return;
    }

    setSending(true);
    try {
      await addNotification({
        title: formData.title,
        message: formData.message,
        audience: formData.audience,
        sentBy: 'Admin', // Replace with actual admin name from auth context
      });
      
      // Send email if selected
      if (formData.sendEmail) {
        const users = await fetchUsers();
        let targetEmails = [];
        
        users.forEach(user => {
          if (!user.email) return;
          if (formData.audience === 'all') {
            targetEmails.push(user.email);
          } else if (formData.audience === 'students' && user.role === 'student') {
            targetEmails.push(user.email);
          } else if (formData.audience === 'teachers' && user.role === 'teacher') {
            targetEmails.push(user.email);
          } else if (['ss1', 'ss2', 'ss3', 'utme'].includes(formData.audience) && user.role === 'student' && user.classLevel === formData.audience) {
            targetEmails.push(user.email);
          }
        });
        
        if (targetEmails.length > 0) {
          await emailService.sendBulkEmails(targetEmails, formData.title, formData.message);
        }
      }
      
      // Reset form
      setFormData({
        title: '',
        message: '',
        audience: 'all',
        customClass: '',
        sendEmail: false,
      });
      
      alert('Notification sent successfully!');
    } catch (error) {
      console.error('Error sending notification:', error);
      alert('Failed to send notification. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const columns = [
    {
      header: 'Title',
      accessor: 'title',
      render: (value) => <span className="font-medium text-gray-900">{value}</span>,
    },
    {
      header: 'Audience',
      accessor: 'audience',
      render: (value) => (
        <span className="text-gray-700">{value}</span>
      ),
    },
    {
      header: 'Sent By',
      accessor: 'sentBy',
      render: (value) => <span className="text-gray-600">{value}</span>,
    },
    {
      header: 'Sent Date',
      accessor: 'sentAt',
      render: (value) => {
        const date = value?.toDate?.() || new Date(value);
        return (
          <span className="text-gray-600">
            {date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </span>
        );
      },
    },
    {
      header: 'Status',
      accessor: 'status',
      render: (value) => (
        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
          value === 'sent' ? 'bg-green-100 text-green-800' :
          value === 'scheduled' ? 'bg-blue-100 text-blue-800' :
          'bg-red-100 text-red-800'
        }`}>
          {value?.charAt(0).toUpperCase() + value?.slice(1)}
        </span>
      ),
    },
  ];

  // Calculate stats for display
  const thisMonth = notifications.filter(n => {
    const date = n.createdAt?.toDate?.() || new Date(n.createdAt);
    const now = new Date();
    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  }).length;

  const thisWeek = notifications.filter(n => {
    const date = n.createdAt?.toDate?.() || new Date(n.createdAt);
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return date >= weekAgo;
  }).length;

  return (
    <div className="w-full">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-darkNavy mb-2 font-poppins">Notifications</h1>
        <p className="text-gray-500">Send announcements and alerts to users</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Send Notification Form */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <MdNotifications className="text-primary text-xl" />
            </div>
            <h2 className="text-xl font-bold text-darkNavy">Send New Notification</h2>
          </div>

          <div className="space-y-5">
            {/* Title */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Notification Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter notification title..."
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Write your message here..."
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
              />
              <p className="text-sm text-gray-500 mt-1">{formData.message.length} characters</p>
            </div>

            {/* Target Audience */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Target Audience <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.audience}
                onChange={(e) => setFormData({ ...formData, audience: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              >
                <option value="all">All Users</option>
                <option value="students">All Students</option>
                <option value="ss1">SS1 Students</option>
                <option value="ss2">SS2 Students</option>
                <option value="ss3">SS3 Students</option>
                <option value="utme">UTME Students</option>
                <option value="teachers">All Teachers</option>
                <option value="subscribed">Subscribed Students Only</option>
              </select>
            </div>

            {/* Send Email Checkbox */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="sendEmail"
                checked={formData.sendEmail}
                onChange={(e) => setFormData({ ...formData, sendEmail: e.target.checked })}
                className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary"
              />
              <label htmlFor="sendEmail" className="text-sm text-gray-700">
                Also send as Email Notification
              </label>
            </div>

            {/* Send Button */}
            <button
              onClick={handleSendNotification}
              disabled={!formData.title || !formData.message || sending}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-blue-700 transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <MdSend size={20} />
              {sending ? 'Sending...' : 'Send Notification'}
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-1 gap-4">
          <div className="bg-white rounded-2xl border border-gray-200 p-4 md:p-5 flex flex-col justify-between">
            <div>
              <p className="text-xs md:text-sm text-gray-500 mb-1">Total Sent</p>
              <p className="text-xl md:text-2xl font-bold text-darkNavy">{stats.total}</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-4 md:p-5 flex flex-col justify-between">
            <div>
              <p className="text-xs md:text-sm text-gray-500 mb-1">This Month</p>
              <p className="text-xl md:text-2xl font-bold text-darkNavy">{thisMonth}</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-4 md:p-5 flex flex-col justify-between">
            <div>
              <p className="text-xs md:text-sm text-gray-500 mb-1">This Week</p>
              <p className="text-xl md:text-2xl font-bold text-darkNavy">{thisWeek}</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-4 md:p-5 flex flex-col justify-between">
            <div>
              <p className="text-xs md:text-sm text-gray-500 mb-1">Today</p>
              <p className="text-xl md:text-2xl font-bold text-green-600">{stats.todaySent}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Notification History */}
      <div className="mt-8">
        <h2 className="text-xl font-bold text-darkNavy mb-4">Notification History</h2>
        <div className="bg-white rounded-2xl border border-gray-200 overflow-x-auto">
          {loading ? (
            <div className="p-12 text-center">
              <p className="text-gray-400">Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-400">No notifications sent yet</p>
            </div>
          ) : (
            <DataTable columns={columns} data={notifications} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Notifications;
